import React, { useState, useMemo } from 'react';
import Gift from 'lucide-react/dist/esm/icons/gift.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import Zap from 'lucide-react/dist/esm/icons/zap.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import Settings from 'lucide-react/dist/esm/icons/settings.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';
import ChevronUp from 'lucide-react/dist/esm/icons/chevron-up.js';
import Image from 'lucide-react/dist/esm/icons/image.js';
import Camera from 'lucide-react/dist/esm/icons/camera.js';
import Plus from 'lucide-react/dist/esm/icons/plus.js';
import RotateCcw from 'lucide-react/dist/esm/icons/rotate-ccw.js';
import { useTranslations } from '../../contexts/TranslationContext';
import { useContent } from '../../contexts/ContentContext';
import { useLanguage } from '../../contexts/LanguageContext';
import InlineTranslationEdit from '../InlineTranslationEdit';
import AdditionalDrawDetailModal from '../AdditionalDrawDetailModal';
import ImagePickerModal from '../ImagePickerModal';
import RichContentRenderer from '../RichContentRenderer';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { parseDrawDate, classifyDrawTiming, formatCompactDrawDate } from '../../utils/dateFormat';
import { formatCurrency } from '../../utils/formatCurrency';
import { useCountdown } from '../../hooks/useCountdown';

export function resolveDrawImage(draw, config = {}) {
  if (!draw) return null;
  const customImages = config?.customDrawImages || {};
  if (draw.id && customImages[draw.id]) return customImages[draw.id];
  if (draw.draw_number != null && customImages[`draw_${draw.draw_number}`]) return customImages[`draw_${draw.draw_number}`];
  if (draw.image_url) return draw.image_url;
  return config?.defaultPrizeImage || null;
}

// Value-based gradient tiers
function getTierStyle(prizeValue) {
  if (prizeValue >= 10000) return { border: 'border-yellow-400/40 dark:border-yellow-400/30', glow: 'shadow-yellow-500/20', hoverBorder: 'hover:border-yellow-400/80', gradient: 'from-yellow-400 via-amber-400 to-orange-300', labelKey: 'bonusDraws.tierGrand' };
  if (prizeValue >= 5000) return { border: 'border-purple-400/40 dark:border-purple-400/30', glow: 'shadow-purple-500/20', hoverBorder: 'hover:border-purple-400/80', gradient: 'from-purple-400 via-violet-400 to-fuchsia-300', labelKey: 'bonusDraws.tierPremium' };
  if (prizeValue >= 1000) return { border: 'border-emerald-400/40 dark:border-emerald-400/30', glow: 'shadow-emerald-500/20', hoverBorder: 'hover:border-emerald-400/80', gradient: 'from-emerald-400 via-green-400 to-teal-300', labelKey: 'bonusDraws.tierFeatured' };
  return { border: 'border-gray-200 dark:border-white/10', glow: '', hoverBorder: 'hover:border-amber-400/60', gradient: 'from-amber-400 to-orange-300', labelKey: null };
}

function formatPrize(value, language) {
  return formatCurrency(value, { language });
}

export const EARLY_BIRD_STYLES = {
  gold: {
    badge: 'bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 text-amber-600 dark:text-amber-300 border-amber-500/30 shadow-sm shadow-amber-500/10',
    icon: 'text-amber-500 dark:text-amber-300',
    name: 'Gold Glow'
  },
  solid: {
    badge: 'bg-gradient-to-r from-amber-400 to-yellow-400 text-black font-extrabold shadow-md border-amber-300',
    icon: 'text-black fill-current',
    name: 'Solid Gold'
  },
  fire: {
    badge: 'bg-gradient-to-r from-orange-500/20 via-red-500/20 to-orange-500/20 text-orange-600 dark:text-orange-400 border-orange-500/30 shadow-sm shadow-orange-500/10',
    icon: 'text-orange-500 dark:text-orange-400',
    name: 'Fiery Flame'
  },
  emerald: {
    badge: 'bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-emerald-500/20 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 shadow-sm shadow-emerald-500/10',
    icon: 'text-emerald-600 dark:text-emerald-400',
    name: 'Emerald'
  },
  purple: {
    badge: 'bg-gradient-to-r from-purple-500/20 via-fuchsia-500/20 to-purple-500/20 text-purple-700 dark:text-purple-300 border-purple-500/30 shadow-sm shadow-purple-500/10',
    icon: 'text-purple-600 dark:text-purple-400',
    name: 'Royal Purple'
  },
  cyan: {
    badge: 'bg-gradient-to-r from-cyan-500/20 via-blue-500/20 to-cyan-500/20 text-cyan-700 dark:text-cyan-300 border-cyan-500/30 shadow-sm shadow-cyan-500/10',
    icon: 'text-cyan-600 dark:text-cyan-400',
    name: 'Electric Cyan'
  }
};

function TimingBadge({ draw, mainDraw, earlyBirdStyle = 'gold', editMode = false, handleTranslationUpdate, c, t, language }) {
  const timing = classifyDrawTiming(draw, mainDraw);
  const styleConfig = EARLY_BIRD_STYLES[earlyBirdStyle] || EARLY_BIRD_STYLES.gold;

  if (timing.isEarlyBird) {
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold border ${styleConfig.badge}`}>
        <Zap className={`w-3 h-3 ${styleConfig.icon} fill-current`} />
        <InlineTranslationEdit
          translationKey="BonusDrawsEarlyBirdLabel"
          value={c('BonusDrawsEarlyBirdLabel', t('bonusDraws.earlyBird', 'Early Bird'))}
          fallback="Early Bird"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </span>
    );
  }

  if (timing.isConcurrent) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-blue-500/15 text-blue-700 dark:text-blue-300 border border-blue-500/20">
        <Calendar className="w-3 h-3 text-blue-500 dark:text-blue-400" />
        <InlineTranslationEdit
          translationKey="BonusDrawsDrawnWithMainLabel"
          value={c('BonusDrawsDrawnWithMainLabel', t('bonusDraws.drawnWithMain', 'Drawn with Main Draw'))}
          fallback="Drawn with Main Draw"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </span>
    );
  }

  if (timing.drawDate) {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-purple-500/15 text-purple-700 dark:text-purple-300 border border-purple-500/20">
        <Calendar className="w-3 h-3 text-purple-500 dark:text-purple-400" />
        {formatCompactDrawDate(timing.drawDate, language)}
      </span>
    );
  }

  return null;
}

function PrizeCountdown({ drawDate, draw }) {
  const targetDate = parseDrawDate(drawDate ? { draw_date: drawDate } : draw);
  const { timeLeft, isComplete } = useCountdown(targetDate);
  const { t } = useTranslations();

  if (!timeLeft || isComplete) return null;

  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-white/60">
      <Clock className="w-3.5 h-3.5 text-amber-500" />
      <span>
        {t('bonusDraws.buyBefore', 'Buy before')}{' '}
        {timeLeft.days > 0 && `${timeLeft.days}d `}
        {timeLeft.hours}h {timeLeft.minutes}m
      </span>
    </div>
  );
}

function FeaturedPrizeCard({ draw, mainDraw, earlyBirdStyle, onClick, onBuyTickets, showCountdown, showTimingBadges, language, editMode, handleTranslationUpdate, c, t, imageSize = 'md' }) {
  const tier = getTierStyle(Number(draw.prize_value || draw.current_jackpot || 0));
  const prizeText = formatPrize(draw.prize_value || draw.current_jackpot, language);

  // Map configured image size to responsive width layout classes aligned with our system
  const widthClasses = {
    sm: 'md:w-1/4 h-48 md:h-auto',
    md: 'md:w-2/5 h-56 md:h-auto',
    lg: 'md:w-1/2 h-64 md:h-auto',
    xl: 'md:w-3/5 h-80 md:h-auto',
  }[imageSize] || 'md:w-2/5 h-56 md:h-auto';

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={`relative group cursor-pointer rounded-3xl border ${tier.border} ${tier.hoverBorder} bg-white dark:bg-white/[0.04] backdrop-blur-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl dark:hover:shadow-amber-500/10 shadow-lg`}
      onClick={onClick}
    >
      <div className="flex flex-col md:flex-row">
        {/* Prize image */}
        {draw.image_url && (
          <div className={`${widthClasses} relative overflow-hidden bg-black/10`}>
            <img
              src={draw.image_url}
              alt={draw.title}
              loading="lazy"
              className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-transparent via-transparent to-black/40 pointer-events-none" />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 p-6 md:p-8 flex flex-col justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {tier.labelKey && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20">
                  <Trophy className="w-3.5 h-3.5 text-yellow-500" />
                  <span className={`text-[11px] font-bold uppercase tracking-wider bg-gradient-to-r ${tier.gradient} bg-clip-text text-transparent`}>
                    {t(tier.labelKey, tier.labelKey === 'bonusDraws.tierGrand' ? 'Grand Prize' : tier.labelKey === 'bonusDraws.tierPremium' ? 'Premium' : 'Featured')}
                  </span>
                </div>
              )}
              {showTimingBadges && (
                <TimingBadge
                  draw={draw}
                  mainDraw={mainDraw}
                  earlyBirdStyle={earlyBirdStyle}
                  editMode={editMode}
                  handleTranslationUpdate={handleTranslationUpdate}
                  c={c}
                  t={t}
                  language={language}
                />
              )}
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-1 group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors">
              {draw.title}
            </h3>
            {draw.draw_number != null && (
              <p className="text-xs text-gray-500 dark:text-white/50 mb-3">
                {t('bonusDraws.drawNumber', 'Draw Number')}: #{draw.draw_number}
              </p>
            )}

            {prizeText && (
              <div className="inline-block px-3.5 py-2 rounded-xl bg-gray-50 dark:bg-white/10 border border-gray-200 dark:border-white/15 mb-3">
                <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-white/50 mb-0.5 font-semibold">
                  <InlineTranslationEdit
                    translationKey="BonusDrawsPrizeValueLabel"
                    value={c('BonusDrawsPrizeValueLabel')}
                    fallback="Prize Value"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                </p>
                <div className="text-xl md:text-2xl font-extrabold text-gray-900 dark:text-white">
                  {prizeText}
                </div>
              </div>
            )}

            {draw.description && (
              <RichContentRenderer
                html={draw.description}
                variant="banner"
                size="sm"
                className="text-sm text-gray-700 dark:text-white/80 mb-4"
              />
            )}
          </div>

          <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-white/10 mt-2">
            <div className="flex items-center gap-4">
              {showCountdown && draw.draw_cutoff_date && (
                <PrizeCountdown draw={draw} drawDate={draw.draw_cutoff_date} />
              )}
              {draw.total_tickets_sold != null && (
                <span className="text-xs text-gray-500 dark:text-white/40">
                  {draw.total_tickets_sold.toLocaleString()} tickets sold
                </span>
              )}
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onBuyTickets();
                }}
                className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-amber-500 px-4 py-2 text-sm font-bold text-gray-950 transition-colors hover:bg-amber-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900"
              >
                <Ticket className="h-4 w-4" />
                <span>{t('bonusDraws.buyTickets', 'Buy Tickets')}</span>
              </button>
              <div className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 dark:text-white/60 group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors">
                <span>{t('bonusDraws.viewDetails', 'Details')}</span>
                <ChevronRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PrizeGridCard({ draw, mainDraw, earlyBirdStyle, onClick, showCountdown, showTimingBadges, delay, language, editMode, handleTranslationUpdate, c, t }) {
  const tier = getTierStyle(Number(draw.prize_value || draw.current_jackpot || 0));
  const prizeText = formatPrize(draw.prize_value || draw.current_jackpot, language);

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={`relative group cursor-pointer rounded-2xl border ${tier.border} ${tier.hoverBorder} bg-white dark:bg-white/[0.04] backdrop-blur-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:hover:shadow-amber-500/10 shadow-md animate-fade-in-up ${delay} flex flex-col justify-between`}
      onClick={onClick}
    >
      <div>
        {/* Prize image */}
        {draw.image_url && (
          <div className="h-40 relative overflow-hidden bg-black/10">
            <img
              src={draw.image_url}
              alt={draw.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
            {prizeText && (
              <div className="absolute bottom-2 right-3 text-lg font-extrabold text-white drop-shadow-md">
                {prizeText}
              </div>
            )}
            {showTimingBadges && (
              <div className="absolute top-2 left-2">
                <TimingBadge
                  draw={draw}
                  mainDraw={mainDraw}
                  earlyBirdStyle={earlyBirdStyle}
                  editMode={editMode}
                  handleTranslationUpdate={handleTranslationUpdate}
                  c={c}
                  t={t}
                  language={language}
                />
              </div>
            )}
          </div>
        )}

        <div className="p-4">
          {!draw.image_url && showTimingBadges && (
            <div className="mb-2">
              <TimingBadge
                draw={draw}
                mainDraw={mainDraw}
                earlyBirdStyle={earlyBirdStyle}
                editMode={editMode}
                handleTranslationUpdate={handleTranslationUpdate}
                c={c}
                t={t}
                language={language}
              />
            </div>
          )}

          <h4 className="font-bold text-gray-900 dark:text-white text-base leading-tight mb-1 line-clamp-2 group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors">
            {draw.title}
          </h4>
          {draw.draw_number != null && (
            <p className="text-[10px] text-gray-500 dark:text-white/50 mb-2">
              {t('bonusDraws.drawNumber', 'Draw Number')}: #{draw.draw_number}
            </p>
          )}

          {!draw.image_url && prizeText && (
            <div className="mb-2">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-white/50 mb-0.5 font-semibold">
                <InlineTranslationEdit
                  translationKey="BonusDrawsPrizeValueLabel"
                  value={c('BonusDrawsPrizeValueLabel')}
                  fallback="Prize Value"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
              <div className="text-xl font-extrabold text-gray-900 dark:text-white">
                {prizeText}
              </div>
            </div>
          )}

          {draw.description && (
            <RichContentRenderer
              html={draw.description}
              variant="banner"
              size="sm"
              className="text-xs text-gray-600 dark:text-white/60 max-h-10 overflow-hidden mb-3"
            />
          )}
        </div>
      </div>

      <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-white/10 flex items-center justify-between">
        {showCountdown && draw.draw_cutoff_date ? (
          <PrizeCountdown draw={draw} drawDate={draw.draw_cutoff_date} />
        ) : (
          <span />
        )}

        <div className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 dark:text-white/60 group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors">
          <span>{t('bonusDraws.viewDetails', 'Details')}</span>
          <ChevronRight className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1" />
        </div>
      </div>
    </div>
  );
}

function PrizeListItem({ draw, mainDraw, earlyBirdStyle, onClick, showCountdown, showTimingBadges, delay, language, editMode, handleTranslationUpdate, c, t }) {
  const tier = getTierStyle(Number(draw.prize_value || draw.current_jackpot || 0));
  const prizeText = formatPrize(draw.prize_value || draw.current_jackpot, language);

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
      className={`group cursor-pointer rounded-2xl border ${tier.border} ${tier.hoverBorder} bg-white dark:bg-white/[0.04] backdrop-blur-lg overflow-hidden transition-all duration-300 hover:bg-gray-50/80 dark:hover:bg-white/[0.08] hover:shadow-md animate-fade-in-up ${delay}`}
      onClick={onClick}
    >
      <div className="flex items-center gap-4 p-4">
        {draw.image_url && (
          <div className="w-16 h-16 flex-shrink-0 rounded-xl overflow-hidden bg-black/10">
            <img
              src={draw.image_url}
              alt={draw.title}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
            />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <h4 className="font-bold text-gray-900 dark:text-white text-sm leading-tight truncate group-hover:text-amber-500 dark:group-hover:text-amber-400 transition-colors">
              {draw.title}
            </h4>
            {showTimingBadges && (
              <TimingBadge
                draw={draw}
                mainDraw={mainDraw}
                earlyBirdStyle={earlyBirdStyle}
                editMode={editMode}
                handleTranslationUpdate={handleTranslationUpdate}
                c={c}
                t={t}
                language={language}
              />
            )}
          </div>

          {draw.draw_number != null && (
            <p className="text-[10px] text-gray-500 dark:text-white/50">
              {t('bonusDraws.drawNumber', 'Draw Number')}: #{draw.draw_number}
            </p>
          )}
          {draw.description && (
            <RichContentRenderer
              html={draw.description}
              variant="banner"
              size="sm"
              className="text-xs text-gray-600 dark:text-white/60 max-h-5 overflow-hidden mt-0.5"
            />
          )}
          {showCountdown && draw.draw_cutoff_date && (
            <div className="mt-1">
              <PrizeCountdown draw={draw} drawDate={draw.draw_cutoff_date} />
            </div>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {prizeText && (
            <div className={`text-base md:text-lg font-extrabold bg-gradient-to-r ${tier.gradient} bg-clip-text text-transparent`}>
              {prizeText}
            </div>
          )}
          <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-amber-400 transition-transform group-hover:translate-x-1" />
        </div>
      </div>
    </div>
  );
}

const DELAY_CLASSES = ['', 'animation-delay-100', 'animation-delay-200', 'animation-delay-300'];

export default function AdditionalPrizesShowcase({
  bonusDraws,
  mainDraw = null,
  editMode = false,
  handleTranslationUpdate = () => {},
  config = {},
  onUpdateConfig = () => {},
  onBuyTickets = () => {}
}) {
  const { t } = useTranslations();
  const { c } = useContent();
  const { language } = useLanguage();
  const [selectedDraw, setSelectedDraw] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pickerTarget, setPickerTarget] = useState(null);

  // Directly verify admin status so the sprocket is reliably rendered whenever editing or in admin context, aligning perfectly with standard section behaviors
  const { isAdmin: resolvedIsAdmin } = useIsAdmin();
  const showSettings = editMode || resolvedIsAdmin;

  const {
    layout = 'spotlight',
    showCountdown = true,
    showHeroBadge = true,
    showTimingBadges = true,
    earlyBirdStyle = 'gold',
    maxVisible = 6,
    spotlightImageSize = 'md'
  } = config;

  const sortedDraws = useMemo(() => {
    if (!bonusDraws || bonusDraws.length === 0) return [];
    return [...bonusDraws]
      .sort((a, b) => (Number(b.prize_value || b.current_jackpot || 0)) - (Number(a.prize_value || a.current_jackpot || 0)))
      .slice(0, maxVisible)
      .map(d => ({
        ...d,
        image_url: resolveDrawImage(d, config)
      }));
  }, [bonusDraws, maxVisible, config]);

  const handleDrawClick = (draw) => {
    if (!editMode) setSelectedDraw(draw);
  };

  if (!sortedDraws.length) return null;

  const featured = layout === 'spotlight' ? sortedDraws[0] : null;
  const gridDraws = layout === 'spotlight' ? sortedDraws.slice(1) : sortedDraws;

  return (
    <div className="relative pt-6">
      {/* Inline Admin Settings Panel */}
      {showSettings && (
        <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-40 flex justify-center">
          <div className="relative">
            <button
              type="button"
              onClick={() => setSettingsOpen(!settingsOpen)}
              className="rounded-b-lg bg-black/85 backdrop-blur-md border border-t-0 border-white/20 px-3 py-1.5 text-[10px] font-bold text-white/70 hover:text-white hover:bg-black transition-colors flex items-center gap-1 cursor-pointer shadow-[0_4px_12px_rgba(0,0,0,0.5)] border-x"
            >
              <Settings className="w-3.5 h-3.5 animate-[spin_10s_linear_infinite] text-amber-400" />
              {settingsOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>

            {settingsOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 w-80 p-4 rounded-2xl bg-black/90 border border-white/20 text-white shadow-2xl backdrop-blur-xl z-50 text-xs space-y-3 animate-fade-in">
                <div className="font-bold text-sm text-amber-400 border-b border-white/10 pb-2 flex items-center gap-1.5">
                  <Settings className="w-4 h-4 text-amber-400 animate-pulse" /> {t('admin.bonusDrawsSettings.title', 'Bonus Draws Settings')}
                </div>

                {/* Layout mode */}
                <div>
                  <label className="block text-gray-400 mb-1 font-medium">{t('admin.bonusDrawsSettings.layoutStyle', 'Layout Style')}</label>
                  <div className="grid grid-cols-3 gap-1 p-1 bg-white/10 rounded-lg">
                    {[
                      { id: 'spotlight', label: t('admin.bonusDrawsSettings.spotlight', 'Spotlight') },
                      { id: 'grid', label: t('admin.bonusDrawsSettings.grid', 'Grid') },
                      { id: 'list', label: t('admin.bonusDrawsSettings.list', 'List') }
                    ].map((mode) => (
                      <button
                        key={mode.id}
                        type="button"
                        onClick={() => onUpdateConfig({ layout: mode.id })}
                        className={`py-1 text-center rounded text-xs font-semibold transition-colors ${
                          (layout || 'spotlight') === mode.id
                            ? 'bg-amber-500 text-black shadow'
                            : 'text-gray-300 hover:text-white'
                        }`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Spotlight Image Size (render ONLY in spotlight mode) */}
                {layout === 'spotlight' && (
                  <div className="space-y-1">
                    <label className="block text-gray-400 mb-1 font-medium">{t('admin.bonusDrawsSettings.spotlightImageSize', 'Spotlight Image Size')}</label>
                    <div className="grid grid-cols-4 gap-1 p-1 bg-white/10 rounded-lg">
                      {[
                        { id: 'sm', label: 'SM' },
                        { id: 'md', label: 'MD' },
                        { id: 'lg', label: 'LG' },
                        { id: 'xl', label: 'XL' }
                      ].map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => onUpdateConfig({ spotlightImageSize: item.id })}
                          className={`py-1 text-center rounded text-xs font-semibold transition-colors ${
                            (spotlightImageSize || 'md') === item.id
                              ? 'bg-amber-500 text-black shadow'
                              : 'text-gray-300 hover:text-white'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Early Bird Badge Style */}
                <div>
                  <label className="block text-gray-400 mb-1 font-medium">{t('admin.bonusDrawsSettings.earlyBirdBadgeStyle', 'Early Bird Badge Style')}</label>
                  <div className="grid grid-cols-3 gap-1.5">
                    {Object.entries(EARLY_BIRD_STYLES).map(([key, styleObj]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => onUpdateConfig({ earlyBirdStyle: key })}
                        className={`py-1.5 px-2 rounded-lg text-[11px] font-bold border transition-all truncate ${
                          (earlyBirdStyle || 'gold') === key
                            ? `${styleObj.badge} ring-2 ring-amber-400/80`
                            : 'bg-white/5 border-white/10 text-gray-400 hover:text-white'
                        }`}
                      >
                        {t(`admin.heroSettings.badgeStyles.${key}`, styleObj.name)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Toggles */}
                <div className="space-y-2 pt-1 border-t border-white/10">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-gray-300">{t('admin.bonusDrawsSettings.heroTicketBadge', 'Hero / Ticket Badge')}</span>
                    <input
                      type="checkbox"
                      checked={showHeroBadge !== false}
                      onChange={(e) => onUpdateConfig({ showHeroBadge: e.target.checked })}
                      className="rounded bg-white/20 border-white/30 text-amber-500 focus:ring-0 cursor-pointer"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-gray-300">{t('admin.bonusDrawsSettings.earlyBirdTimingBadges', 'Early Bird / Timing Badges')}</span>
                    <input
                      type="checkbox"
                      checked={showTimingBadges !== false}
                      onChange={(e) => onUpdateConfig({ showTimingBadges: e.target.checked })}
                      className="rounded bg-white/20 border-white/30 text-amber-500 focus:ring-0 cursor-pointer"
                    />
                  </label>

                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-gray-300">{t('admin.bonusDrawsSettings.countdownTimers', 'Countdown Timers')}</span>
                    <input
                      type="checkbox"
                      checked={showCountdown !== false}
                      onChange={(e) => onUpdateConfig({ showCountdown: e.target.checked })}
                      className="rounded bg-white/20 border-white/30 text-amber-500 focus:ring-0 cursor-pointer"
                    />
                  </label>
                </div>

                {/* Max visible */}
                <div className="pt-2 border-t border-white/10">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-400">{t('admin.bonusDrawsSettings.maxVisible', 'Max Visible')}</span>
                    <span className="font-mono text-amber-400">{maxVisible}</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="12"
                    value={maxVisible}
                    onChange={(e) => onUpdateConfig({ maxVisible: parseInt(e.target.value, 10) })}
                    className="w-full accent-amber-500"
                  />
                </div>

                {/* Fallback Image */}
                <div className="pt-2 border-t border-white/10 space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 font-medium">{t('admin.bonusDrawsSettings.defaultFallbackImage', 'Default Fallback Image')}</span>
                    {config.defaultPrizeImage && (
                      <button
                        type="button"
                        onClick={() => onUpdateConfig({ defaultPrizeImage: '' })}
                        className="text-red-400 hover:text-red-300 font-medium text-[10px]"
                      >
                        {t('admin.sections.reset', 'Reset')}
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setPickerTarget({ type: 'default' })}
                    className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded bg-white/10 text-white font-medium hover:bg-white/20 transition-all border border-white/10 cursor-pointer"
                  >
                    {config.defaultPrizeImage ? (
                      <>
                        <RotateCcw className="w-3.5 h-3.5 text-amber-400" /> {t('admin.bonusDrawsSettings.changeFallbackImage', 'Change Fallback Image')}
                      </>
                    ) : (
                      <>
                        <Plus className="w-3.5 h-3.5 text-amber-400" /> {t('admin.bonusDrawsSettings.chooseFallbackImage', 'Choose Fallback Image')}
                      </>
                    )}
                  </button>
                  {config.defaultPrizeImage && (
                    <div className="rounded overflow-hidden border border-white/15 h-16 relative bg-black/40">
                      <img src={config.defaultPrizeImage} alt="Fallback preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                </div>

                {/* Per-Draw Custom Images */}
                <div className="pt-2 border-t border-white/10 space-y-2">
                  <span className="text-gray-400 font-medium block">{t('admin.bonusDrawsSettings.customPrizeImages', 'Custom Prize Images')}</span>
                  <div className="space-y-2 max-h-[160px] overflow-y-auto pr-1">
                    {sortedDraws.map((d) => {
                      const customImages = config.customDrawImages || {};
                      const hasCustom = !!customImages[d.id];
                      const resolvedImg = resolveDrawImage(d, config);

                      return (
                        <div key={d.id} className="flex items-center justify-between gap-2 p-1.5 rounded bg-white/5 border border-white/10">
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {resolvedImg ? (
                              <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0 bg-black/50 border border-white/10">
                                <img src={resolvedImg} alt="" className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded bg-gray-800 flex items-center justify-center flex-shrink-0 border border-white/10">
                                <Image className="w-4 h-4 text-white/40" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-bold truncate text-[11px] text-white leading-tight">{d.title}</p>
                              <p className="text-[9px] text-gray-400 leading-none mt-0.5">
                                {hasCustom ? t('admin.bonusDrawsSettings.customImage', 'Custom Image') : t('admin.bonusDrawsSettings.originalImage', 'Original Image')}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button
                              type="button"
                              onClick={() => setPickerTarget({ type: 'draw', drawId: d.id })}
                              className="p-1 rounded bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
                              title={t('admin.bonusDrawsSettings.setCustomImage', 'Set Custom Image')}
                            >
                              <Camera className="w-3.5 h-3.5 text-amber-300" />
                            </button>
                            {hasCustom && (
                              <button
                                type="button"
                                onClick={() => {
                                  const currentCustom = { ...config.customDrawImages };
                                  delete currentCustom[d.id];
                                  onUpdateConfig({ customDrawImages: currentCustom });
                                }}
                                className="p-1 rounded bg-red-950/40 hover:bg-red-900/40 text-red-400 transition-colors cursor-pointer"
                                title={t('admin.bonusDrawsSettings.removeCustomImage', 'Remove Custom Image')}
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {pickerTarget && (
        <ImagePickerModal
          onClose={() => setPickerTarget(null)}
          onSelect={(url) => {
            if (pickerTarget.type === 'default') {
              onUpdateConfig({ defaultPrizeImage: url });
            } else if (pickerTarget.type === 'draw') {
              const currentCustom = config.customDrawImages || {};
              onUpdateConfig({
                customDrawImages: { ...currentCustom, [pickerTarget.drawId]: url }
              });
            }
            setPickerTarget(null);
          }}
        />
      )}

      {/* Header */}
      <div className="flex items-center gap-2.5 mb-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-amber-500/10 dark:bg-amber-400/10 border border-amber-500/20">
          <Gift className="w-5 h-5 text-amber-500 dark:text-amber-400" />
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
          <InlineTranslationEdit
            translationKey="BonusDrawsTitle"
            value={c('BonusDrawsTitle', t('bonusDraws.title'))}
            fallback="Additional Prizes"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h2>
      </div>

      <p className="text-sm text-gray-600 dark:text-white/70 mb-6 max-w-2xl">
        <InlineTranslationEdit
          translationKey="BonusDrawsSubtitle"
          value={c('BonusDrawsSubtitle', t('bonusDraws.subtitle'))}
          fallback="Every ticket purchase gives you automatic entry into these bonus draws for extra chances to win!"
          onUpdate={handleTranslationUpdate}
          editMode={editMode}
        />
      </p>

      {/* Featured prize (spotlight mode only) */}
      {featured && (
        <div className="mb-6">
          <FeaturedPrizeCard
            draw={featured}
            mainDraw={mainDraw}
            earlyBirdStyle={earlyBirdStyle}
            onClick={() => handleDrawClick(featured)}
            onBuyTickets={onBuyTickets}
            showCountdown={showCountdown}
            showTimingBadges={showTimingBadges}
            language={language}
            editMode={editMode}
            handleTranslationUpdate={handleTranslationUpdate}
            c={c}
            t={t}
            imageSize={spotlightImageSize}
          />
        </div>
      )}

      {/* Grid or List */}
      {gridDraws.length > 0 && (
        layout === 'list' ? (
          <div className="space-y-3">
            {gridDraws.map((draw, idx) => (
              <PrizeListItem
                key={draw.id}
                draw={draw}
                mainDraw={mainDraw}
                earlyBirdStyle={earlyBirdStyle}
                onClick={() => handleDrawClick(draw)}
                showCountdown={showCountdown}
                showTimingBadges={showTimingBadges}
                delay={DELAY_CLASSES[Math.min(idx, DELAY_CLASSES.length - 1)]}
                language={language}
                editMode={editMode}
                handleTranslationUpdate={handleTranslationUpdate}
                c={c}
                t={t}
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {gridDraws.map((draw, idx) => (
              <PrizeGridCard
                key={draw.id}
                draw={draw}
                mainDraw={mainDraw}
                earlyBirdStyle={earlyBirdStyle}
                onClick={() => handleDrawClick(draw)}
                showCountdown={showCountdown}
                showTimingBadges={showTimingBadges}
                delay={DELAY_CLASSES[Math.min(idx, DELAY_CLASSES.length - 1)]}
                language={language}
                editMode={editMode}
                handleTranslationUpdate={handleTranslationUpdate}
                c={c}
                t={t}
              />
            ))}
          </div>
        )
      )}

      <AdditionalDrawDetailModal
        isOpen={!!selectedDraw}
        onClose={() => setSelectedDraw(null)}
        draw={selectedDraw}
        mainDraw={mainDraw}
        earlyBirdStyle={earlyBirdStyle}
      />
    </div>
  );
}
